import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Send, Download, Share2, Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff,
  FileSignature, Sparkles, Monitor, Smartphone, FileText, ExternalLink, Save,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getProposal, updateProposal, getProposalBlockTypes, downloadProposalPdf, saveProposalAsTemplate,
  createProposalSection, getProposalSections,
} from "../api/prospectingReportsApi";
import ProposalBlockRenderer from "../components/ProposalBlockRenderer";
import ProposalBlockEditor from "../components/ProposalBlockEditor";
import SendProposalModal from "../components/SendProposalModal";
import ShareProposalModal from "../components/ShareProposalModal";
import GeeVeeStrategyPanel from "../components/GeeVeeStrategyPanel";
import CreateAuthorityModal from "../components/CreateAuthorityModal";
import ReadReceiptBadge from "../components/ReadReceiptBadge";
import { COLLINGS_BRAND } from "../collingsBrand";

const TEAL = "#0f8d9b";
const uid = () => `b${Math.random().toString(36).slice(2, 10)}`;

const ProposalBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState(0);
  const [blockTypes, setBlockTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [authorityOpen, setAuthorityOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [device, setDevice] = useState("desktop"); // desktop | mobile | pdf
  const [savedSections, setSavedSections] = useState([]);
  const saveTimer = useRef(null);

  const editable = proposal && proposal.status === "draft";

  useEffect(() => {
    getProposal(id)
      .then(({ data }) => {
        setProposal(data);
        setBlocks(data.blocks || []);
        setRevision(data.revision || 0);
      })
      .catch(() => toast.error("Could not load the proposal."))
      .finally(() => setLoading(false));
    getProposalBlockTypes().then(({ data }) => setBlockTypes(data?.block_types || [])).catch(() => {});
    getProposalSections().then(({ data }) => setSavedSections(data?.results || [])).catch(() => {});
  }, [id]);

  const persist = useCallback(async (nextBlocks) => {
    setSaving("saving");
    try {
      const { data } = await updateProposal(id, { blocks: nextBlocks, base_revision: revision });
      setRevision(data.revision || 0);
      setProposal(data);
      setSaving("saved");
      setTimeout(() => setSaving(""), 1500);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("This proposal changed elsewhere; reloading.");
        const fresh = e.response.data?.current;
        if (fresh) { setBlocks(fresh.blocks || []); setRevision(fresh.revision || 0); setProposal(fresh); }
      } else {
        toast.error("Could not save.");
      }
      setSaving("");
    }
  }, [id, revision]);

  const scheduleSave = useCallback((nextBlocks) => {
    if (!editable) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(nextBlocks), 1200);
  }, [editable, persist]);

  const updateBlocks = (next) => { setBlocks(next); scheduleSave(next); };

  const setBlockData = (i, data) => updateBlocks(blocks.map((b, j) => (j === i ? { ...b, data } : b)));
  // Price guide suggestion: set the estimate block AND add the chosen sales to
  // Comparables (creating the section after the estimate if there isn't one),
  // in one update so neither change overwrites the other.
  const applyEstimate = (i, data, rows) => {
    let next = blocks.map((b, j) => (j === i ? { ...b, data } : b));
    const ci = next.findIndex((b) => b.type === "comparables");
    if (ci >= 0) {
      const existing = next[ci].data?.items || [];
      const seen = new Set(existing.map((r) => (r.address || "").toLowerCase()));
      const add = rows.filter((r) => !seen.has((r.address || "").toLowerCase()));
      next = next.map((b, j) => (j === ci ? { ...b, data: { ...b.data, items: [...existing, ...add] } } : b));
    } else {
      next = [...next];
      next.splice(i + 1, 0, { id: uid(), type: "comparables", version: 1, visible: true,
        data: { intro: "Recent sales that support this price guide.", use_cma: false, items: rows } });
    }
    updateBlocks(next);
  };
  const toggleVisible = (i) => updateBlocks(blocks.map((b, j) => (j === i ? { ...b, visible: b.visible === false } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    updateBlocks(next);
    setSelected(j);
  };
  const remove = (i) => {
    const next = blocks.filter((_, j) => j !== i);
    updateBlocks(next);
    setSelected(Math.max(0, i - 1));
  };
  const addBlock = (type) => {
    const next = [...blocks, { id: uid(), type, version: 1, visible: true, data: {} }];
    updateBlocks(next);
    setSelected(next.length - 1);
    setAddOpen(false);
  };
  const addSavedSection = (section) => {
    const b = section.block || {};
    const next = [...blocks, { id: uid(), type: b.type, version: b.version || 1, visible: true, data: b.data || {} }];
    updateBlocks(next);
    setSelected(next.length - 1);
    setAddOpen(false);
  };

  const handlePdf = async () => {
    try {
      const { data } = await downloadProposalPdf(id);
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Could not build the PDF.");
    }
  };

  const openPreview = () => {
    const url = proposal.share_url || (proposal.share_token && `${window.location.origin}/${proposal.share_token}`);
    if (url) window.open(url, "_blank", "noopener");
  };

  const saveAsSection = async (block) => {
    const name = window.prompt("Save this section to your library. Name:", (block.type || "section").replace(/_/g, " "));
    if (!name) return;
    try {
      await createProposalSection({ name, block });
      toast.success("Saved to Sections.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the section.");
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Save this proposal as a template. Name:", `${proposal.title || "Proposal"} template`);
    if (!name) return;
    try {
      await saveProposalAsTemplate(id, { name });
      toast.success("Saved as a template.");
    } catch (e) {
      toast.error("Could not save as a template.");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!proposal) return <div className="p-6 text-gray-500">Proposal not found.</div>;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/prospecting/proposals")} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4" /> Proposals
          </button>
          <ReadReceiptBadge proposal={proposal} large />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 min-w-[52px]">{saving === "saving" ? "Saving…" : saving === "saved" ? "Saved" : ""}</span>
          <button onClick={() => setStrategyOpen(true)}
            title="GeeVee Strategy — analyse this proposal"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#0f8d9b]/30 text-sm font-semibold text-[#0f8d9b] bg-[#eaf7f8]">
            <Sparkles className="w-4 h-4" /> GeeVee
          </button>
          <button onClick={openPreview} title="Open the vendor-facing preview" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700"><ExternalLink className="w-4 h-4" /> Preview</button>
          <button onClick={saveAsTemplate} title="Save this design as a reusable template" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700"><Save className="w-4 h-4" /> Template</button>
          <button onClick={() => setShareOpen(true)}
            className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-semibold ${proposal.is_public ? "border-[#2f9e6b]/40 text-[#2f9e6b] bg-[#eaf7ef]" : "border-gray-200 text-gray-700"}`}>
            <Share2 className="w-4 h-4" /> {proposal.is_public ? "Live" : "Share"}
          </button>
          <button onClick={handlePdf} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700"><Download className="w-4 h-4" /> PDF</button>
          {proposal.status === "accepted" ? (
            proposal.authority_envelope ? (
              <button onClick={() => navigate(`/esign/authority/${proposal.authority_envelope}`)}
                title="Open the listing authority created from this proposal"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: "#2f9e6b" }}>
                <FileSignature className="w-4 h-4" /> Open listing authority
              </button>
            ) : (
              <button onClick={() => setAuthorityOpen(true)}
                title="Prepare a draft listing authority in e-sign, pre-filled from this proposal"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: "#2f9e6b" }}>
                <FileSignature className="w-4 h-4" /> Create listing authority
              </button>
            )
          ) : (
            <button onClick={() => setSendOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: TEAL }}><Send className="w-4 h-4" /> Send</button>
          )}
        </div>
      </div>

      {!editable && (
        <div className="mb-4 rounded-lg bg-[#fff4e5] border border-[#f0d9a8] text-[#8a5a12] text-sm px-4 py-2.5">
          This proposal has been sent and is read-only. Duplicate it from the list to make changes.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* Section list + editor */}
        <div>
          <div className="rounded-xl border border-gray-100 bg-white p-2 mb-3">
            {blocks.map((b, i) => (
              <div key={b.id || i}
                className={`flex items-center gap-1 px-2 py-2 rounded-lg cursor-pointer ${i === selected ? "bg-[#eaf1f3]" : "hover:bg-gray-50"}`}
                onClick={() => setSelected(i)}>
                <span className="flex-1 text-sm font-medium text-gray-700 capitalize">{(b.type || "").replace(/_/g, " ")}</span>
                <button onClick={(e) => { e.stopPropagation(); toggleVisible(i); }} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Toggle">
                  {b.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} disabled={!editable} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} disabled={!editable} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(i); }} disabled={!editable} className="p-1 text-gray-300 hover:text-red-500 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="relative">
              <button onClick={() => setAddOpen((v) => !v)} disabled={!editable}
                className="w-full mt-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg border border-dashed border-gray-200 text-sm font-semibold text-[#0f8d9b] disabled:opacity-40">
                <Plus className="w-4 h-4" /> Add section
              </button>
              {addOpen && (
                <div className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-gray-100 bg-white shadow-lg py-1">
                  {blockTypes.map((t) => (
                    <button key={t.type} onClick={() => addBlock(t.type)} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{t.label}</button>
                  ))}
                  {savedSections.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400 border-t border-gray-100 mt-1">Saved sections</div>
                      {savedSections.map((s) => (
                        <button key={s.id} onClick={() => addSavedSection(s)} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{s.name}</button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {blocks[selected] && editable && (
            <div className="rounded-xl border border-gray-100 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 capitalize">{(blocks[selected].type || "").replace(/_/g, " ")}</p>
                <button type="button" onClick={() => saveAsSection(blocks[selected])}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#0f8d9b] hover:underline" title="Save this section to your library">
                  <Save className="w-3.5 h-3.5" /> Save as section
                </button>
              </div>
              <ProposalBlockEditor block={blocks[selected]} onChange={(data) => setBlockData(selected, data)} proposalId={id} proposal={proposal}
                onApplyEstimate={(data, rows) => applyEstimate(selected, data, rows)} />
            </div>
          )}
        </div>

        {/* Live preview — edge-to-edge so the full-bleed bands read as they will
            on the public page. Device toggle mirrors Proply (Desktop/Mobile/PDF). */}
        <div>
          <div className="flex items-center justify-center gap-1 mb-3">
            <div className="inline-flex rounded-lg bg-gray-100 p-1">
              {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Mobile"], ["pdf", FileText, "PDF"]].map(([k, Icon, lbl]) => (
                <button key={k} onClick={() => (k === "pdf" ? (setDevice("pdf"), handlePdf()) : setDevice(k))}
                  title={lbl}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold ${device === k ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>
                  <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{lbl}</span>
                </button>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border border-gray-100 bg-white overflow-hidden ${device === "mobile" ? "max-w-[390px] mx-auto" : ""}`}>
          <ProposalBlockRenderer
            blocks={blocks}
            theme={proposal.theme_colour}
            branding={COLLINGS_BRAND}
            agent={proposal.agent}
            comparables={[]}
            acceptSlot={<div className="mt-3 text-sm opacity-80">Accept / request-callback buttons appear here for the vendor.</div>}
          />
          </div>
        </div>
      </div>

      {strategyOpen && (
        <GeeVeeStrategyPanel proposalId={id} onClose={() => setStrategyOpen(false)} />
      )}

      {sendOpen && (
        <SendProposalModal proposal={proposal} onClose={() => setSendOpen(false)}
          onSent={(data) => { setProposal(data); setSendOpen(false); }} />
      )}
      {authorityOpen && (
        <CreateAuthorityModal proposal={proposal} onClose={() => setAuthorityOpen(false)}
          onCreated={(envelopeId) => { setAuthorityOpen(false); navigate(`/esign/authority/${envelopeId}`); }} />
      )}
      {shareOpen && (
        <ShareProposalModal proposal={proposal} onClose={() => setShareOpen(false)}
          onChanged={(patch) => setProposal((p) => ({ ...p, ...patch }))} />
      )}
    </div>
  );
};

export default ProposalBuilderPage;
